/**
 * Outfitting view-model (spec §7.3, §8; plan §6 M2-18): the 9 slots with the fitted Mk and power draw, the power
 * bar (draw / budget), and per slot the offers: fit from the locker, buy from the station, sell from the locker
 * at `sellFraction`, each with a stat-diff preview (`statDiff`) and the power check before `fitModule` refuses.
 * Recondition (economy plan SY-5b): one row per Serviceable hold component that has a bill, with the goods bill and
 * what the hold lacks ("Needs 0.8 t Electronics"); none while `career.json goods.tradeEnabled` is off.
 * Salvaged (economy plan SY-5c, D8): one row per Intact installable hold component (Install; a weapon as PDC or
 * laser), per salvaged module in the locker (Fit, Sell at component value) and per fitted one (fit a bought module
 * over it to free it), each with its source, condition, Mk and "resells at component value: X". Install, fit and
 * sell need no goods, so they are not gated by the goods flag (only the outfitting service).
 * Scrap (M8 plan MC-c7, D-526): one row per locker module with something to recover, with the materials it returns
 * (`scrapLines`) and the `module.scrap` craft command; none while the goods flag is off.
 */
import { statDiff, type FitStatKey, type StatDiff } from '../../career/derive'
import {
  fitSalvaged,
  installComponent,
  powerBudget,
  powerDraw,
  salvagedOf,
  salvagedSaleValue,
  scrapLines,
  scrapModule,
  buyModule,
  sellSalvaged,
  type ScrapLine,
} from '../../career/fitting'
import type { CraftCommandMap } from '../../craft/hook'
import type { CmdResult } from '../../career/result'
import { SALVAGED_MODULE_KINDS } from '../../career/schema'
import { reconditionComponent, reconditionLabel, reconditionLines, reconditionShortfall } from '../../career/recondition'
import type { CargoComponent, Career, MkComponentType, SalvagedModule } from '../../career/types'
import { resolveModule } from '../../data/m2'
import {
  COMPONENT_TYPES,
  MK_COMPONENT_TYPES,
  SLOT_IDS,
  moduleId as toModuleId,
  parseModuleId,
  patternModuleId,
  type M2Data,
  type ModuleDef,
  type ModuleId,
  type ModuleKind,
  type SlotId,
  type StationDef,
  type StationId,
  WARE_IDS,
} from '../../data/m2Schemas'
import { UI_CONFIG, type UiConfig } from '../config'
import { formatCr } from '../format'
import { CONDITION_BAND, conditionBand } from '../../salvage/generate'
import { frameLabel } from './seeds'
import { z } from 'zod'
import { GOOD_NAMES } from '../../career/ops'
import { GOOD_MASS_T, goodT, isGoodId, type GoodId } from '../../career/parts'
import { EPSILON } from '../../career/ops'
import { formatTonnes } from '../../career/result'
import type { FabCarryId, FabOrder, GoodOrWare } from '../../career/types'
import { completeFab, deliverFab, fabOrderHolds, fabOrders, remoteBerths, startFab, type RemoteBerth, type RemotePull } from '../../craft/fabOrder'
import { heldSalvage, quoteFab, remoteDraw, type FabInputPlan, type FabQuote, type FabRemoteDraw } from '../../craft/quote'
import { isComponentType, type FabSystem } from '../../craft/recipes'
import { fragmentLadder, isUnlocked, study, studyParts, unlockData, unlockId, PATTERN_RUNGS, type PatternRung, type UnlockData } from '../../craft/unlocks'
import { PATTERN_IDS, type MakerId, type PatternId, type PatternUnlockId } from '../../career/types'
import { shortageRows, type ShortageRow, type ShortageWorld } from '../../craft/shortage'
import type { MarketQuote } from '../../strategic-link/market'
import fabJson from '../screens/fab.json'

export interface SlotRow {
  slot: SlotId
  label: string
  moduleId: ModuleId
  moduleName: string
  mk: number
  power: number
  powerLabel: string
}

export interface PowerBarModel {
  draw: number
  budget: number
  /** Draw / budget, clamped to 0..1 for the bar. */
  fraction: number
  over: boolean
  label: string
}

export interface StatDiffRow {
  stat: FitStatKey
  label: string
  before: string
  after: string
  better: boolean
}

export type OfferAction = 'fit' | 'buy' | 'sell' | FabAction | 'study'

export interface ModuleOffer {
  action: OfferAction
  id: ModuleId
  name: string
  mk: number
  /** Buy: the price; sell: what the locker module fetches; fit: 0. */
  price: number
  label: string
  /** How many of this module the locker holds. */
  owned: number
  /** Why the action would be refused (funds, power), or null. The button stays usable: the command refuses inline. */
  warning: string | null
  /**
   * False when the action can never succeed here, whatever the player does on this station (PF-5: a Mk III Buy or
   * Fabricate outside a fabworks or yards system; a Study without enough parts): the button is disabled and
   * `warning` is the reason, shown as its hint. Funds and power keep the button usable (the command refuses inline).
   */
  enabled: boolean
  /** The stat preview of fitting this module into the slot (empty for sell). */
  diff: StatDiffRow[]
  testId: string
  /** Fabrication (MC-c5): the order this offer acts on (null for Fabricate), and the materials preview; absent otherwise. */
  fab?: FabPreview
  /** A faction-pattern variant row (MC-f4): its pattern, unlock state and the study command; absent otherwise. */
  pattern?: PatternPreview
}

export interface OutfittingModel {
  /** The station offers outfitting (else only the fit summary shows). */
  available: boolean
  stationName: string
  slots: SlotRow[]
  power: PowerBarModel
  /** Recondition rows (SY-5b): empty while the goods flag is off or the station has no outfitting. */
  recondition: ReconditionRow[]
  /** Salvaged rows (SY-5c): empty without outfitting. */
  salvaged: SalvagedRow[]
  /** Scrap rows (MC-c7): empty while the goods flag is off or without outfitting. */
  scrap: ScrapRow[]
  /** M7 S33 (PC-1): "KT-3 frame T-0388 · registration RG-2291-K" on the ship screen, or null. */
  frame: string | null
}

/** A Serviceable hold component that can be reconditioned to Intact for goods (economy plan SY-5b). */
export interface ReconditionRow {
  /** `CargoComponent.id` (the `recondition` command's argument). */
  id: string
  /** "Reactor Mk II · 62%". */
  name: string
  /** The goods bill: "1.8 t Electronics, 0.6 t Drive parts". */
  bill: string
  label: string
  /** The hold carries the whole bill (the button is usable). */
  enabled: boolean
  /** What the hold lacks ("Needs 0.8 t Electronics"), or null. */
  warning: string | null
  testId: string
}

const MK_NAMES = ['', ' Mk I', ' Mk II', ' Mk III'] as const

/** The recondition rows at `here` (SY-5b): Serviceable, unsealed hold components whose type has a bill. */
export function reconditionRows(data: M2Data, career: Career, here: StationDef): ReconditionRow[] {
  if (!data.career.goods.tradeEnabled || !here.services.includes('outfitting')) return []
  const rows: ReconditionRow[] = []
  for (const c of career.cargo.components) {
    if (c.sealed === true || c.contractId !== undefined || c.story === true) continue
    if (conditionBand(c.condition, data.salvage.bands) !== CONDITION_BAND.serviceable) continue
    const lines = reconditionLines(career, data, c)
    if (lines === null || lines.length === 0) continue
    const short = reconditionShortfall(lines)
    const bill = reconditionLabel(lines)
    const name = `${data.wares.components[COMPONENT_TYPES.indexOf(c.type)].name}${MK_NAMES[c.mk ?? 0] ?? ''} · ${Math.round(c.condition * 100)}%`
    rows.push({
      id: c.id,
      name,
      bill,
      label: `Recondition ${name} to Intact: ${bill}`,
      enabled: reconditionComponent(career, data, here, c.id).ok,
      warning: short.length > 0 ? `Needs ${reconditionLabel(short)}` : null,
      testId: `outfit-recondition-${c.id}`,
    })
  }
  return rows
}

/** A Salvaged-section command (SY-5c): the `UiActions` call a button makes. */
export type SalvagedCommand =
  | { action: 'install'; id: string; kind: ModuleKind }
  | { action: 'fit'; slot: SlotId; sourceId: string }
  | { action: 'sell'; sourceId: string }

export interface SalvagedButton {
  cmd: SalvagedCommand
  label: string
  /** The command would apply now (the pure function accepts it). */
  enabled: boolean
  /** Why it would be refused (the command's own message), or null. */
  warning: string | null
  testId: string
}

/** One salvaged component or module (SY-5c): in the hold (installable), in the salvaged locker, or fitted. */
export interface SalvagedRow {
  /** `CargoComponent.id`, kept as the module's `sourceId`. */
  id: string
  /** "Reactor Mk II · 95%" (in the hold) or "Power Plant Mk II · 95%" (installed). */
  name: string
  /** "In the hold", "In the locker" or "Fitted: Power plant (…)". */
  where: string
  /** "Source: w9:0". */
  source: string
  /** "Resells at component value: 13,580 cr". */
  resale: string
  /** Install (one per kind), or Fit and Sell; none for a fitted module (fit a bought module over it first). */
  buttons: SalvagedButton[]
  testId: string
}

function isMkType(type: CargoComponent['type']): type is MkComponentType {
  return (MK_COMPONENT_TYPES as readonly string[]).includes(type)
}

function pct(condition: number): string {
  return `${Math.round(condition * 100)}%`
}

function salvagedButton(result: CmdResult<Career>, cmd: SalvagedCommand, label: string, testId: string): SalvagedButton {
  return { cmd, label, enabled: result.ok, warning: result.ok ? null : result.error.message, testId }
}

function moduleRow(data: M2Data, m: SalvagedModule, where: string, buttons: SalvagedButton[]): SalvagedRow {
  return {
    id: m.sourceId,
    name: `${resolveModule(data, m.moduleId).name} · ${pct(m.condition)}`,
    where,
    source: `Source: ${m.sourceId}`,
    resale: `Resells at component value: ${formatCr(salvagedSaleValue(data, m))}`,
    buttons,
    testId: `outfit-salvaged-${m.sourceId}`,
  }
}

/** The slot a module kind fits (each kind fits exactly one slot in `modules.json slots`), or undefined. */
function slotFor(data: M2Data, id: ModuleId): SlotId | undefined {
  const kind = parseModuleId(id).kind
  return SLOT_IDS.find((s) => data.modules.slots[s].includes(kind))
}

/**
 * The Salvaged rows at `here` (SY-5c): Intact installable hold components (contract, sealed and story ones left
 * out), then the salvaged locker (Fit, Sell), then the fitted salvaged modules (no buttons: there is no separate
 * unfit, a bought module fitted over one frees it). Each button's state comes from SY-5a's pure command, so the row
 * refuses exactly as the command will.
 */
export function salvagedRows(data: M2Data, career: Career, here: StationDef, cfg: UiConfig = UI_CONFIG): SalvagedRow[] {
  if (!here.services.includes('outfitting')) return []
  const rows: SalvagedRow[] = []
  for (const c of career.cargo.components) {
    if (c.sealed === true || c.contractId !== undefined || c.story === true || !isMkType(c.type)) continue
    if (conditionBand(c.condition, data.salvage.bands) !== CONDITION_BAND.intact) continue
    const mk = c.mk === 2 || c.mk === 3 ? c.mk : 1
    const kinds = SALVAGED_MODULE_KINDS[c.type]
    const buttons = kinds.map((kind) =>
      salvagedButton(
        installComponent(career, data, here, c.id, kind),
        { action: 'install', id: c.id, kind },
        `Install as ${resolveModule(data, toModuleId(kind, mk)).name}`,
        kinds.length === 1 ? `outfit-install-${c.id}` : `outfit-install-${c.id}-${kind}`,
      ),
    )
    rows.push({
      id: c.id,
      name: `${data.wares.components[COMPONENT_TYPES.indexOf(c.type)].name}${MK_NAMES[mk]} · ${pct(c.condition)}`,
      where: 'In the hold',
      source: `Source: ${c.id}`,
      resale: `Resells at component value: ${formatCr(salvagedSaleValue(data, { type: c.type, condition: c.condition }))}`,
      buttons,
      testId: `outfit-salvaged-${c.id}`,
    })
  }
  const st = salvagedOf(career.ship)
  for (const m of st.locker) {
    const buttons: SalvagedButton[] = []
    const slot = slotFor(data, m.moduleId)
    if (slot !== undefined) {
      const fit = fitSalvaged(career, data, here, slot, m.sourceId)
      buttons.push(salvagedButton(fit, { action: 'fit', slot, sourceId: m.sourceId }, `Fit to ${cfg.slots[slot]}`, `outfit-fitsalvaged-${m.sourceId}`))
    }
    const sell = sellSalvaged(career, data, here, m.sourceId)
    const label = `Sell: ${formatCr(salvagedSaleValue(data, m))}`
    buttons.push(salvagedButton(sell, { action: 'sell', sourceId: m.sourceId }, label, `outfit-sellsalvaged-${m.sourceId}`))
    rows.push(moduleRow(data, m, 'In the locker', buttons))
  }
  for (const slot of SLOT_IDS) {
    const m = st.fitted[slot]
    if (m !== undefined) rows.push(moduleRow(data, m, `Fitted: ${cfg.slots[slot]} (fit a bought module over it to free it)`, []))
  }
  return rows
}

/** How many Salvaged buttons there are (SY-5c): the first items of the `G.salvaged` focus group. */
export function salvagedButtonCount(rows: readonly SalvagedRow[]): number {
  let n = 0
  for (const r of rows) n += r.buttons.length
  return n
}

/** A locker module that can be scrapped into materials in the hold (M8 plan MC-c7, D-526; RD8: no credits). */
export interface ScrapRow {
  /** The bought or fabricated module's id, or the salvaged module's `sourceId`. */
  id: string
  /** "PDC Mk II (×2)" or "Power Plant Mk II · 95% (salvaged)". */
  name: string
  /** What scrapping returns (`career/fitting.ts scrapLines`). */
  lines: ScrapLine[]
  /** "1.6 t Steel, 0.4 t Weapon parts". */
  yields: string
  label: string
  /** The `UiActions.craft` command (`features/scrap`), at the docked station. */
  cmd: CraftCommandMap['module.scrap']
  /** `scrapModule` accepts it now. */
  enabled: boolean
  /** Its refusal (a full hold), or null. */
  warning: string | null
  testId: string
}

/** A scrap yield in tonnes: "1.6 t Steel, 0.4 t Weapon parts" (goods are units of `GOOD_MASS_T` tonnes). */
export function scrapLabel(data: M2Data, lines: readonly ScrapLine[]): string {
  return lines.map((l) => `${formatTonnes(isGoodId(l.id) ? l.qty * GOOD_MASS_T[l.id] : l.qty)} t ${fabItemName(data, l.id)}`).join(', ')
}

/**
 * The Scrap rows at `here` (MC-c7, D-526): one per bought or fabricated module id in the flown ship's locker (in
 * locker order), then one per salvaged locker module, each with what scrapping returns (`scrapLines`) and the
 * `module.scrap` command. A module with nothing to recover (Mk I) has no row; fitted modules never do. Each row's state
 * comes from `scrapModule`, so it refuses exactly as the command will (a full hold). Empty while `goods.tradeEnabled`
 * is off or without outfitting, like Recondition.
 */
export function scrapRows(data: M2Data, career: Career, here: StationDef): ScrapRow[] {
  if (!data.career.goods.tradeEnabled || !here.services.includes('outfitting')) return []
  const rows: ScrapRow[] = []
  const push = (id: string, name: string): void => {
    const lines = scrapLines(career, data, id)
    if (lines === null || lines.length === 0) return
    const r = scrapModule(career, data, here, id)
    const yields = scrapLabel(data, lines)
    rows.push({
      id,
      name,
      lines,
      yields,
      label: `Scrap ${name}: ${yields}`,
      cmd: { kind: 'module.scrap', stationId: here.id, id },
      enabled: r.ok,
      warning: r.ok ? null : r.error.message,
      testId: `outfit-scrap-${id}`,
    })
  }
  const seen = new Set<ModuleId>()
  for (const id of career.ship.locker) {
    if (seen.has(id)) continue
    seen.add(id)
    const n = countOf(career.ship.locker, id)
    push(id, `${resolveModule(data, id).name}${n > 1 ? ` (×${n})` : ''}`)
  }
  for (const m of salvagedOf(career.ship).locker) push(m.sourceId, `${resolveModule(data, m.moduleId).name} · ${pct(m.condition)} (salvaged)`)
  return rows
}

function mkOf(def: ModuleDef): number {
  return def.mk
}

export function powerBar(draw: number, budget: number): PowerBarModel {
  return {
    draw,
    budget,
    fraction: budget > 0 ? Math.min(1, draw / budget) : 1,
    over: draw > budget,
    label: `${draw} / ${budget}`,
  }
}

/** The fit summary (slots and power) of the flown ship, wherever it is: the dashboard's Ship › Loadout reads it in flight. */
export function fitSummary(data: M2Data, career: Career, cfg: UiConfig = UI_CONFIG): { slots: SlotRow[]; power: PowerBarModel } {
  const fitted = career.ship.fitted
  const slots = SLOT_IDS.map((slot): SlotRow => {
    const def = resolveModule(data, fitted[slot])
    return {
      slot,
      label: cfg.slots[slot],
      moduleId: def.id,
      moduleName: def.name,
      mk: mkOf(def),
      power: def.power,
      powerLabel: def.kind === 'power' ? `+${powerBudget(fitted, data)}` : String(def.power),
    }
  })
  return { slots, power: powerBar(powerDraw(fitted, data), powerBudget(fitted, data)) }
}

export function outfittingModel(data: M2Data, career: Career, here: StationDef, cfg: UiConfig = UI_CONFIG): OutfittingModel {
  const { slots, power } = fitSummary(data, career, cfg)
  return {
    available: here.services.includes('outfitting'),
    stationName: here.name,
    slots,
    power,
    recondition: reconditionRows(data, career, here),
    salvaged: salvagedRows(data, career, here, cfg),
    scrap: scrapRows(data, career, here),
    frame: frameLabel(data),
  }
}

/** A preview value in its unit ("×1.15", "85%", "2.0%/s", "120 m", "35 t", "Tier 2", "Mk 3", "+10 m/s", "12"). */
export function formatStat(stat: FitStatKey, v: number): string {
  switch (stat) {
    case 'laserMaxPower':
      return `${Math.round(v * 100)}%`
    case 'spillPerSec':
      return `${(v * 100).toFixed(1)}%/s`
    case 'tractorRange':
    case 'scannerRange':
      return `${Math.round(v)} m`
    case 'cargoCapacityT':
    case 'maxPartMassT':
      return `${Math.round(v * 10) / 10} t`
    case 'maxTier':
      return `Tier ${Math.round(v) + 1}`
    case 'scannerMk':
      return `Mk ${Math.round(v)}`
    case 'maxSpeedAdd':
      return `+${Math.round(v)} m/s`
    case 'powerDraw':
    case 'powerBudget':
      return String(Math.round(v))
    default:
      return `×${(Math.round(v * 100) / 100).toFixed(2)}`
  }
}

function diffRows(diff: readonly StatDiff[]): StatDiffRow[] {
  return diff.map((d) => ({ stat: d.stat, label: d.label, before: formatStat(d.stat, d.before), after: formatStat(d.stat, d.after), better: d.better }))
}

/** The power draw and budget after fitting `id` into `slot`. */
function powerAfter(data: M2Data, career: Career, slot: SlotId, id: ModuleId): { draw: number; budget: number } {
  const fit = { ...career.ship.fitted, [slot]: id }
  return { draw: powerDraw(fit, data), budget: powerBudget(fit, data) }
}

function countOf(list: readonly ModuleId[], id: ModuleId): number {
  let n = 0
  for (const x of list) if (x === id) n++
  return n
}

/**
 * The offers for `slot`: the station's modules for the slot's kinds (all Mks) first, so buying keeps every row where
 * it was, then fit from the locker (one row per module id, the fitted one excluded), then sell from the locker, then
 * Fabricate and the open fab orders here (MC-c5, `fabOffers`: last, so the rows above stay put and the fab rows sit
 * next to their preview), then the faction-pattern variants (MC-f4, `patternOffers`). Every action needs the outfitting
 * service.
 */
export function slotOffers(data: M2Data, career: Career, here: StationDef, slot: SlotId, fab: FabOptions = {}): ModuleOffer[] {
  const kinds = data.modules.slots[slot]
  const fitted = career.ship.fitted[slot]
  const locker = career.ship.locker
  const outfits = here.services.includes('outfitting')
  const lockerIds: ModuleId[] = []
  for (const id of locker) if (!lockerIds.includes(id) && kinds.includes(resolveModule(data, id).kind)) lockerIds.push(id)
  lockerIds.sort()
  const offers: ModuleOffer[] = []
  if (!outfits) return offers

  for (const def of data.modules.modules) {
    if (!kinds.includes(def.kind) || !here.outfitting.includes(def.kind)) continue
    const diff = statDiff(career, data, slot, def.id)
    const p = powerAfter(data, career, slot, def.id)
    const geography = geographyRefusal(buyModule(career, data, here, def.id, fab.system))
    const warnings: string[] = geography === null ? [] : [geography]
    if (career.wallet < def.price) warnings.push(`Needs ${formatCr(def.price)}`)
    if (p.draw > p.budget) warnings.push(`Over the power budget if fitted (${p.draw} of ${p.budget})`)
    offers.push({
      action: 'buy',
      id: def.id,
      name: def.name,
      mk: def.mk,
      price: def.price,
      label: `Buy ${def.name}: ${formatCr(def.price)}`,
      owned: countOf(locker, def.id),
      warning: warnings.length > 0 ? warnings.join(' · ') : null,
      enabled: geography === null,
      diff: def.id === fitted || !diff.ok ? [] : diffRows(diff.value),
      testId: `outfit-buy-${def.id}`,
    })
  }
  for (const id of lockerIds) {
    if (id === fitted) continue
    const def = resolveModule(data, id)
    const diff = statDiff(career, data, slot, id)
    const p = powerAfter(data, career, slot, id)
    offers.push({
      action: 'fit',
      id,
      name: def.name,
      mk: def.mk,
      price: 0,
      label: `Fit ${def.name}`,
      owned: countOf(locker, id),
      warning: p.draw > p.budget ? `Over the power budget: ${p.draw} of ${p.budget}` : null,
      enabled: true,
      diff: diff.ok ? diffRows(diff.value) : [],
      testId: `outfit-fit-${slot}-${id}`,
    })
  }
  for (const id of lockerIds) {
    const def = resolveModule(data, id)
    const price = def.price * data.modules.sellFraction
    offers.push({
      action: 'sell',
      id,
      name: def.name,
      mk: def.mk,
      price,
      label: `Sell ${def.name} from the locker: ${formatCr(price)}`,
      owned: countOf(locker, id),
      warning: null,
      enabled: true,
      diff: [],
      testId: `outfit-sell-${id}`,
    })
  }
  offers.push(...fabOffers(data, career, here, slot, fab))
  offers.push(...patternOffers(data, career, here, slot, fab))
  return offers
}

// ---------------------------------------------------------------------------------------------
// Fabrication (M8 plan MC-c5)
// ---------------------------------------------------------------------------------------------

const fabTextSchema = z.strictObject({
  fabricate: z.string(),
  deliver: z.string(),
  buyMissing: z.string(),
  complete: z.string(),
  cancel: z.string(),
  cancelToBerth: z.string(),
  giveSalvage: z.string(),
  quoteSummary: z.string(),
  orderSummary: z.string(),
  fullStats: z.string(),
  colItem: z.string(),
  colNeed: z.string(),
  colHave: z.string(),
  colHold: z.string(),
  colBerth: z.string(),
  colBuy: z.string(),
  colRemote: z.string(),
  remote: z.string(),
  remoteSameSystem: z.string(),
  carryTag: z.string(),
  noBuy: z.string(),
  salvage: z.string(),
  bandIntact: z.string(),
  bandServiceable: z.string(),
  shortageTitle: z.string(),
  seller: z.string(),
  live: z.string(),
  age: z.string(),
  mining: z.string(),
  grade: z.string(),
  chart: z.string(),
  noSources: z.string(),
  ready: z.string(),
  hints: z.record(z.string(), z.string()),
})
const patternTextSchema = z.strictObject({
  study: z.string(),
  id: z.string(),
  locked: z.string(),
  unlocked: z.string(),
  standing: z.string(),
  studyRoute: z.string(),
  partsMaker: z.string(),
  partsMk3: z.string(),
  partsRush: z.string(),
  serviceable: z.string(),
  fragments: z.string(),
  fabricateOnly: z.string(),
  firstCharter: z.string(),
  opened: z.string(),
  openedMany: z.string(),
  listAnd: z.string(),
  listComma: z.string(),
  fragmentsLeft: z.string(),
  fragmentsOpened: z.string(),
  fragmentsAll: z.string(),
  rungs: z.strictObject({ mk2: z.string(), mk3: z.string() }),
})
const fabUiSchema = z.strictObject({ comment: z.string(), text: fabTextSchema, patternComment: z.string(), pattern: patternTextSchema })

/** Every fabrication line of the Outfitting tab (`ui/screens/fab.json`), parsed once. */
const FAB_UI = fabUiSchema.parse(fabJson)
export const FAB_TEXT: Readonly<z.infer<typeof fabTextSchema>> = Object.freeze(FAB_UI.text)

/** Every pattern-row line of the Outfitting tab (`ui/screens/fab.json pattern`, MC-f4). */
export const PATTERN_TEXT: Readonly<z.infer<typeof patternTextSchema>> = Object.freeze(FAB_UI.pattern)

/** `template` with every `{key}` replaced by `vals[key]`. */
function fill(template: string, vals: Readonly<Record<string, string | number>>): string {
  return template.replace(/\{(\w+)\}/g, (m: string, k: string) => (k in vals ? String(vals[k]) : m))
}

/** A fab command an offer runs (`UiActions.craft`). */
export type FabAction = 'fabricate' | 'deliver' | 'giveSalvage' | 'buyMissing' | 'complete' | 'cancel'

/**
 * What the fab offers read besides the Career (all optional). The live Outfitting tab gets them from the provider
 * the fab-world feature registers (`ui/models/fabWorld.ts`, MC-c5b); without a Reach runtime it passes none.
 */
export interface FabOptions {
  /** The docked system for Mk III geography (`offeredAt`); undefined keeps the any-station rule, like `buyModule`. */
  system?: FabSystem | null
  /** The shortage card's world (MC-c4) and the pilot's system; without it a short carry shows its data hint only. */
  world?: { world: ShortageWorld; systemId: string } | null
  /**
   * Remote berth pull (MC-b7), as `fab.deliver` passes it: once unlocked, stock in the player's other berths within
   * reach counts towards a line (the "Berths in reach" column) before "Buy here"; without it only the hold and this
   * berth count.
   */
  remote?: RemotePull | null
}

/** One material line of a fab preview: what the recipe (or the order) needs and where it is now. */
export interface FabLine {
  id: FabCarryId
  name: string
  /** The recipe's carry (never bought where you fabricate). */
  carry: boolean
  need: string
  /** Delivered to the order so far ("" for a quote). */
  have: string
  hold: string
  berth: string
  /** What the player's other berths in reach cover ("4 t (1 jumps)", the furthest berth drawn), or "" for none. */
  remote: string
  /** Tonnes (whole units for a component) still missing after the order, the hold, the berth and berths in reach. */
  missing: number
  /** Buy missing may buy it here (a good or ware that is not the carry). */
  buyable: boolean
}

/** Where to get one short item (MC-c4's shortage rows, or the item's data hint). */
export interface ShortageCard {
  item: FabCarryId
  title: string
  rows: string[]
  hint: string | null
}

/** The materials preview of a Fabricate offer or an open order's offers. */
export interface FabPreview {
  /** The open order, or null for a Fabricate offer. */
  orderId: string | null
  moduleId: ModuleId
  /** The docked station (where the order is or would be). */
  stationId: StationId
  summary: string
  lines: FabLine[]
  /** The recipe's salvaged alternative (MC-c8) as it stands, or null. */
  salvage: string | null
  /** The salvaged component a Give offer hands over (`fab.deliver { salvage }`), else null. */
  salvageId: string | null
  /** Every line is on hand (delivered, the hold or the berth). */
  ready: boolean
  note: string
  shortage: ShortageCard[]
}

/** The display name of a fab item: a good, an M2 ware or a component type. */
export function fabItemName(data: M2Data, id: FabCarryId): string {
  if (isComponentType(id)) return data.wares.components[COMPONENT_TYPES.indexOf(id)].name
  if (isGoodId(id)) return GOOD_NAMES[id]
  return data.wares.wares[WARE_IDS.indexOf(id)].name
}

function amount(id: FabCarryId, v: number): string {
  return isComponentType(id) ? String(Math.round(v)) : `${formatTonnes(v)} t`
}

/** How much of `id` the hold and the berth at `stationId` hold now (free components only, none an order holds). */
function onHand(career: Career, stationId: string, id: FabCarryId, held: ReadonlySet<string>): { hold: number; berth: number } {
  const entry = career.storage?.[stationId]
  if (isComponentType(id)) {
    const free = (c: CargoComponent): boolean => c.type === id && c.sealed !== true && c.story !== true && c.contractId === undefined && !held.has(c.id)
    return { hold: career.cargo.components.filter(free).length, berth: (entry?.components ?? []).filter(free).length }
  }
  if (isGoodId(id)) return { hold: goodT(career.cargo.parts, id), berth: entry === undefined ? 0 : goodT(entry.goods, id) }
  return { hold: career.cargo.wares[id], berth: entry?.wares[id] ?? 0 }
}

/** What the hold, then the berth, cover of `rem` still to find. */
function localDraw(rem: number, hold: number, berth: number): { h: number; b: number } {
  const h = Math.min(rem, Math.max(0, hold))
  return { h, b: Math.min(rem - h, Math.max(0, berth)) }
}

/** The "Berths in reach" cell: the tonnes drawn and the furthest berth's jumps, or "" for none. */
function remoteLabel(id: FabCarryId, draws: readonly FabRemoteDraw[]): string {
  if (draws.length === 0) return ''
  let qty = 0
  let jumps = 0
  for (const d of draws) {
    qty += d.qty
    if (d.jumps > jumps) jumps = d.jumps
  }
  return fill(jumps === 0 ? FAB_TEXT.remoteSameSystem : FAB_TEXT.remote, { amount: amount(id, qty), jumps })
}

function fabLine(
  data: M2Data,
  id: FabCarryId,
  carry: boolean,
  need: number,
  have: number | null,
  hold: number,
  berth: number,
  remote: readonly FabRemoteDraw[],
): FabLine {
  const rem = Math.max(0, need - (have ?? 0))
  const { h, b } = localDraw(rem, hold, berth)
  let r = 0
  for (const d of remote) r += d.qty
  r = Math.min(rem - h - b, r)
  const missing = rem - h - b - r
  return {
    id,
    name: fabItemName(data, id),
    carry,
    need: amount(id, need),
    have: have === null ? '' : amount(id, have),
    hold: amount(id, h),
    berth: amount(id, b),
    remote: remoteLabel(id, remote),
    missing: missing > EPSILON ? missing : 0,
    buyable: !carry && !isComponentType(id),
  }
}

function quoteLines(data: M2Data, q: FabQuote): FabLine[] {
  const lines: FabLine[] = []
  for (const [id, p] of Object.entries(q.inputs) as [GoodOrWare, FabInputPlan][]) {
    if (id === q.carry.id) continue
    lines.push(fabLine(data, id, false, p.need, null, p.fromHold, p.fromBerth, p.remote))
  }
  const c = q.carry
  if (c.need > 0) lines.push(fabLine(data, c.id, true, c.need, null, c.fromHold, c.fromBerth, c.remote))
  return lines
}

/**
 * An open order's lines: what the hold and the order station's berth hold of each item it still lacks, then what
 * `berths` (remote pull, `remoteBerths`; empty when locked) would cover, nearest first, as `deliverFab` draws them.
 * A component carry is never pulled from another berth (D-490).
 */
function orderLines(data: M2Data, career: Career, order: FabOrder, held: ReadonlySet<string>, berths: readonly RemoteBerth[]): FabLine[] {
  const lines: FabLine[] = []
  const line = (id: FabCarryId, carry: boolean, need: number, have: number): FabLine => {
    const on = onHand(career, order.stationId, id, held)
    const rem = Math.max(0, need - have)
    const { h, b } = localDraw(rem, on.hold, on.berth)
    const pulled = isComponentType(id) ? [] : remoteDraw(career, berths, id, rem - h - b, held)
    return fabLine(data, id, carry, need, have, on.hold, on.berth, pulled)
  }
  for (const [id, need] of Object.entries(order.required) as [GoodOrWare, number][]) {
    if (id === order.carry.id) continue
    lines.push(line(id, false, need, order.delivered[id] ?? 0))
  }
  const c = order.carry
  if (c.need > 0) lines.push(line(c.id, true, c.need, c.have))
  return lines
}

/** The salvage line ("Weapon (Serviceable or better) can stand in for Platinum: 1 on hand."), or null. */
function salvageText(data: M2Data, s: FabQuote['salvage']): string | null {
  if (s === null) return null
  return fill(FAB_TEXT.salvage, {
    component: fabItemName(data, s.component),
    band: s.minBand === 'intact' ? FAB_TEXT.bandIntact : FAB_TEXT.bandServiceable,
    replaces: fabItemName(data, s.replaces),
    count: s.candidates.length,
  })
}

function systemName(world: ShortageWorld, id: string): string {
  return world.galaxy.systems.find((s) => s.id === id)?.name ?? id
}

/** One shortage row (MC-c4) as a line of the card. */
export function shortageLine(world: ShortageWorld, item: string, r: ShortageRow): string {
  const system = systemName(world, r.systemId)
  switch (r.kind) {
    case 'seller': {
      const age = r.live ? FAB_TEXT.live : fill(FAB_TEXT.age, { days: Math.round(r.ageDays ?? 0) })
      return fill(FAB_TEXT.seller, { system, jumps: r.jumps, unit: formatCr(r.unit ?? 0), age })
    }
    case 'mining':
      return fill(FAB_TEXT.mining, { system, jumps: r.jumps, grade: r.grade === null ? '' : fill(FAB_TEXT.grade, { grade: r.grade }) })
    case 'chart':
      return fill(FAB_TEXT.chart, { system, jumps: r.jumps, item })
  }
}

/**
 * The shortage cards of a preview's missing lines. With a world: MC-c4's rows for every missing line (an input's
 * sellers within 2 jumps; the carry's sellers, mining sources and what to chart). Without one: only the carry, with
 * its data hint (an input the station sells is the "Buy here" column's job).
 */
function shortageCards(data: M2Data, career: Career, here: StationDef, lines: readonly FabLine[], opts: FabOptions): ShortageCard[] {
  const cards: ShortageCard[] = []
  const w = opts.world ?? null
  for (const l of lines) {
    if (l.missing <= 0) continue
    if (w === null && !l.carry) continue
    const hint = FAB_TEXT.hints[l.id] ?? null
    let rows: string[] = []
    if (w !== null) {
      const found = shortageRows(career, data, w.world, { systemId: w.systemId, stationId: here.id, item: l.id, role: l.carry ? 'carry' : 'input' })
      rows = found.map((r) => shortageLine(w.world, l.name, r))
    }
    if (rows.length === 0 && hint === null) rows = [FAB_TEXT.noSources]
    cards.push({ item: l.id, title: fill(FAB_TEXT.shortageTitle, { item: l.name }), rows, hint })
  }
  return cards
}

function fabPreview(
  data: M2Data,
  career: Career,
  here: StationDef,
  head: { moduleId: ModuleId; orderId: string | null; summary: string },
  lines: FabLine[],
  salvage: FabQuote['salvage'],
  opts: FabOptions,
): FabPreview {
  return {
    orderId: head.orderId,
    moduleId: head.moduleId,
    stationId: here.id,
    summary: head.summary,
    lines,
    salvage: salvageText(data, salvage),
    salvageId: null,
    ready: lines.every((l) => l.missing <= 0),
    note: FAB_TEXT.fullStats,
    shortage: shortageCards(data, career, here, lines, opts),
  }
}

function refusalOf(r: CmdResult<Career>): string | null {
  return r.ok ? null : r.error.message
}

/** The Mk III geography refusal (`detail.reason: 'mk3Geography'`: no fabworks or yards in the system), else null. */
function geographyRefusal(r: CmdResult<Career>): string | null {
  return !r.ok && r.error.detail?.reason === 'mk3Geography' ? r.error.message : null
}

/** A Fabricate offer's warning and state: disabled (PF-5) only by Mk III geography, which nothing here can change. */
function startOffer(r: CmdResult<Career>): { warning: string | null; enabled: boolean } {
  return { warning: refusalOf(r), enabled: geographyRefusal(r) === null }
}

/**
 * The fab offers for `slot` at `here` (MC-c5): a Fabricate offer per Mk II/III module of the slot's kinds that has a
 * recipe and that the station outfits (credits, materials, the salvaged alternative, the shortage card and the full
 * stats), then per open fab order here for those kinds: Deliver, Give salvage (MC-c8: a qualifying component is on
 * hand and the order holds none), Buy missing (a buyable input is still missing), Complete and Cancel. Each warning
 * is the pure command's own refusal, so a row refuses exactly as the command will; the button stays usable and the
 * command refuses inline, except a Mk III outside a fabworks or yards system (PF-5: disabled, the refusal as its
 * hint). Empty without outfitting.
 */
export function fabOffers(data: M2Data, career: Career, here: StationDef, slot: SlotId, opts: FabOptions = {}): ModuleOffer[] {
  if (!here.services.includes('outfitting')) return []
  const kinds = data.modules.slots[slot]
  const locker = career.ship.locker
  const fitted = career.ship.fitted[slot]
  const out: ModuleOffer[] = []
  const diffOf = (id: ModuleId): StatDiffRow[] => {
    if (id === fitted) return []
    const d = statDiff(career, data, slot, id)
    return d.ok ? diffRows(d.value) : []
  }
  // MC-b7 remote pull: one jump search per build; an order here draws the same berths (its station is `here`).
  const remote = opts.remote ?? null
  const berths = remote === null ? [] : remoteBerths(career, here.id, remote)
  for (const def of data.modules.modules) {
    if (!kinds.includes(def.kind) || !here.outfitting.includes(def.kind)) continue
    const q = quoteFab(career, data, here, def.id, null, undefined, berths)
    if (q === null) continue
    const credits = formatCr(q.credits)
    const summary = fill(FAB_TEXT.quoteSummary, { credits, price: formatCr(q.price) })
    out.push({
      action: 'fabricate',
      id: def.id,
      name: def.name,
      mk: def.mk,
      price: q.credits,
      label: fill(FAB_TEXT.fabricate, { name: def.name, credits }),
      owned: countOf(locker, def.id),
      ...startOffer(startFab(career, data, here, def.id, opts.system)),
      diff: diffOf(def.id),
      testId: `outfit-fab-${def.id}`,
      fab: fabPreview(data, career, here, { moduleId: def.id, orderId: null, summary }, quoteLines(data, q), q.salvage, opts),
    })
  }
  const held = heldSalvage(career)
  for (const order of fabOrders(career)) {
    if (order.stationId !== here.id) continue
    const def = resolveModule(data, order.moduleId)
    if (!kinds.includes(def.kind)) continue
    const salvage = quoteFab(career, data, here, def.id, null)?.salvage ?? null
    const summary = fill(FAB_TEXT.orderSummary, { id: order.id, credits: formatCr(order.paid) })
    const base = fabPreview(data, career, here, { moduleId: def.id, orderId: order.id, summary }, orderLines(data, career, order, held, berths), salvage, opts)
    const offer = (action: FabAction, label: string, warning: string | null, testId: string, fab: FabPreview = base): ModuleOffer => ({
      action,
      id: def.id,
      name: def.name,
      mk: def.mk,
      price: action === 'cancel' ? order.paid : 0,
      label,
      owned: countOf(locker, def.id),
      warning,
      enabled: true,
      diff: action === 'complete' ? diffOf(def.id) : [],
      testId,
      fab,
    })
    out.push(offer('deliver', fill(FAB_TEXT.deliver, { name: def.name }), refusalOf(deliverFab(career, order.id, remote)), `outfit-fabdeliver-${order.id}`))
    const give = salvage !== null && order.salvageIn.length === 0 ? (salvage.candidates[0] ?? null) : null
    if (give !== null && salvage !== null) {
      const c = [...career.cargo.components, ...(career.storage?.[here.id]?.components ?? [])].find((x) => x.id === give)
      const what = c === undefined ? fabItemName(data, salvage.component) : `${fabItemName(data, c.type)} · ${pct(c.condition)}`
      const label = fill(FAB_TEXT.giveSalvage, { component: what, replaces: fabItemName(data, salvage.replaces) })
      out.push(offer('giveSalvage', label, null, `outfit-fabsalvage-${order.id}`, { ...base, salvageId: give }))
    }
    if (base.lines.some((l) => l.buyable && l.missing > 0)) {
      out.push(offer('buyMissing', fill(FAB_TEXT.buyMissing, { name: def.name }), null, `outfit-fabbuy-${order.id}`))
    }
    out.push(offer('complete', fill(FAB_TEXT.complete, { name: def.name }), refusalOf(completeFab(career, order.id)), `outfit-fabcomplete-${order.id}`))
    // PF-3: an order that holds goods or a salvaged part says where cancel leaves them (D-490: the berth).
    const cancelText = fabOrderHolds(order) ? FAB_TEXT.cancelToBerth : FAB_TEXT.cancel
    out.push(offer('cancel', fill(cancelText, { name: def.name, credits: formatCr(order.paid) }), null, `outfit-fabcancel-${order.id}`))
  }
  return out
}

/** `UiActions.quoteGood`: the strategic quote for a good at the docked station. */
export type QuoteGood = (good: GoodId, qty: number, side: 'buy' | 'sell') => MarketQuote | null

/** The "Buy here" cell of a line: what the docked station sells of the missing tonnes, with its price, or the dash. */
export function fabBuyLabel(line: FabLine, quoteGood: QuoteGood | undefined): string {
  if (!line.buyable || line.missing <= 0 || quoteGood === undefined || !isGoodId(line.id)) return FAB_TEXT.noBuy
  const q = quoteGood(line.id, line.missing, 'buy')
  if (q === null || q.qty <= 0) return FAB_TEXT.noBuy
  return `${formatTonnes(Math.min(q.qty, line.missing))} t · ${formatCr(q.total)}`
}

// ---------------------------------------------------------------------------------------------
// Faction-pattern variants (M8 plan MC-f4)
// ---------------------------------------------------------------------------------------------

/** One unlock route of a pattern rung as the preview lists it. */
export interface PatternRouteLine {
  route: 'standing' | 'study' | 'fragments'
  text: string
  /** The route opens the rung now (standing or fragments), or study would apply now. */
  met: boolean
}

/** The pattern part of a variant row (MC-f4). */
export interface PatternPreview {
  pattern: PatternId
  rung: PatternRung
  /** "Pattern: Hot (varro) · pdc.mk2.varro". */
  idLine: string
  unlocked: boolean
  /** Locked: the routes that open it, each with where the player stands; unlocked: empty. */
  routes: PatternRouteLine[]
  /** "Locked. Unlocks by any one of:" or "Unlocked: fabricate it here." */
  status: string
  /** "2 Serviceable would count once reconditioned to Intact.", or null. */
  hint: string | null
  /** The study command (locked, with a study route), or null. */
  study: { pattern: PatternId; rung: PatternRung } | null
  /** Study would apply now (`craft/unlocks.ts study` accepts it: enough Intact qualifying parts in the hold). */
  canStudy: boolean
  note: string
}

function makerName(maker: MakerId, cfg: UiConfig): string {
  return maker === 'first_charter' ? PATTERN_TEXT.firstCharter : cfg.factions[maker]
}

/** The unlock routes of a locked rung with where the player stands, and the study result (null: no study route). */
function lockedRoutes(
  data: M2Data,
  career: Career,
  here: StationDef,
  pattern: PatternId,
  rung: PatternRung,
  have: number,
  cfg: UiConfig,
  unlocks: UnlockData,
): { routes: PatternRouteLine[]; result: CmdResult<Career> | null } {
  const p = unlocks.patterns.patterns[pattern]
  const routes: PatternRouteLine[] = []
  if (p === undefined) return { routes, result: null }
  if (p.routes.includes('standing') && p.standingFaction !== null) {
    const need = unlocks.patterns.standingReq[rung]
    const standing = career.standings[p.standingFaction]
    const text = fill(PATTERN_TEXT.standing, { need, faction: cfg.factions[p.standingFaction], have: Math.round(standing) })
    routes.push({ route: 'standing', text, met: standing >= need })
  }
  const result = p.routes.includes('study') ? study(career, data, here, pattern, rung, unlocks) : null
  if (result !== null) {
    const template = p.maker === 'first_charter' ? PATTERN_TEXT.partsRush : rung === 'mk3' ? PATTERN_TEXT.partsMk3 : PATTERN_TEXT.partsMaker
    const parts = fill(template, { maker: makerName(p.maker, cfg) })
    routes.push({ route: 'study', text: fill(PATTERN_TEXT.studyRoute, { need: unlocks.study.partsPerStudy, parts, have }), met: result.ok })
  }
  if (p.routes.includes('fragments')) {
    const at = fragmentLadder(unlocks).indexOf(unlockId(pattern, rung))
    if (at >= 0) {
      const need = (at + 1) * unlocks.blueprints.fragmentsPerRung
      const held = career.blueprints?.fragments ?? 0
      routes.push({ route: 'fragments', text: fill(PATTERN_TEXT.fragments, { have: held, need }), met: held >= need })
    }
  }
  return { routes, result }
}

/**
 * The faction-pattern variant rows of `slot` at an outfitting station (MC-f4), in `PATTERN_IDS` order, Mk II then
 * Mk III, one per pattern module of the slot's kinds (`patternModuleId`; name and stat diff through `resolveModule`).
 * An unlocked variant (`craft/unlocks.ts isUnlocked`: the store, standing or fragments) is a Fabricate offer with the
 * fab preview (`fab.start` with the pattern id; `startFab`'s refusal as the warning). A locked one is a Study offer
 * (`craft { kind: 'pattern.study', pattern, rung }`) whose preview lists every route with where the player stands:
 * the standing needed and held, the Intact parts of the maker in the hold (`studyParts`: Mk III parts for Mk III,
 * Rush-pattern ones for `holdfast_old`), the fragment count. Its button is enabled only when `study` accepts it, and
 * the warning is the command's own refusal. Appended after every other offer, so the rows above keep their place.
 * Empty without outfitting.
 */
export function patternOffers(
  data: M2Data,
  career: Career,
  here: StationDef,
  slot: SlotId,
  opts: FabOptions = {},
  cfg: UiConfig = UI_CONFIG,
  unlocks: UnlockData = unlockData(),
): ModuleOffer[] {
  if (!here.services.includes('outfitting')) return []
  const kinds = data.modules.slots[slot]
  const locker = career.ship.locker
  const fitted = career.ship.fitted[slot]
  const out: ModuleOffer[] = []
  for (const pattern of PATTERN_IDS) {
    const p = unlocks.patterns.patterns[pattern]
    if (p === undefined) continue
    for (const kind of kinds) {
      if (!p.modules.includes(kind)) continue
      for (const rung of PATTERN_RUNGS) {
        const id = patternModuleId(kind, rung === 'mk2' ? 2 : 3, pattern)
        const def = resolveModule(data, id)
        const d = id === fitted ? null : statDiff(career, data, slot, id)
        const diff = d !== null && d.ok ? diffRows(d.value) : []
        const unlocked = isUnlocked(career, unlockId(pattern, rung), unlocks)
        const head = { pattern, rung, idLine: fill(PATTERN_TEXT.id, { pattern: p.name, key: pattern, id }), unlocked, note: PATTERN_TEXT.fabricateOnly }
        const row = { id, name: def.name, mk: def.mk, owned: countOf(locker, id), diff }
        if (unlocked) {
          const q = quoteFab(career, data, here, id, null)
          if (q === null) continue
          const credits = formatCr(q.credits)
          const summary = fill(FAB_TEXT.quoteSummary, { credits, price: formatCr(q.price) })
          out.push({
            ...row,
            action: 'fabricate',
            price: q.credits,
            label: fill(FAB_TEXT.fabricate, { name: def.name, credits }),
            ...startOffer(startFab(career, data, here, id, opts.system)),
            testId: `outfit-fab-${id}`,
            fab: fabPreview(data, career, here, { moduleId: id, orderId: null, summary }, quoteLines(data, q), q.salvage, opts),
            pattern: { ...head, routes: [], status: PATTERN_TEXT.unlocked, hint: null, study: null, canStudy: false },
          })
          continue
        }
        const need = unlocks.study.partsPerStudy
        const have = p.routes.includes('study') ? studyParts(career, data, pattern, rung, unlocks).length : 0
        const { routes, result } = lockedRoutes(data, career, here, pattern, rung, have, cfg, unlocks)
        const serviceable = result !== null && !result.ok ? Number(result.error.detail?.serviceable ?? 0) : 0
        out.push({
          ...row,
          action: 'study',
          price: 0,
          label: fill(PATTERN_TEXT.study, { name: def.name, have, need }),
          warning: result === null ? null : refusalOf(result),
          enabled: result?.ok === true,
          testId: `outfit-study-${id}`,
          pattern: {
            ...head,
            routes,
            status: PATTERN_TEXT.locked,
            hint: serviceable > 0 ? fill(PATTERN_TEXT.serviceable, { count: serviceable }) : null,
            study: result === null ? null : { pattern, rung },
            canStudy: result?.ok === true,
          },
        })
      }
    }
  }
  return out
}

// ---------------------------------------------------------------------------------------------
// Result lines (PF-5, M8 playtest findings 8 and 9): the station notice after a Study and after a data-core read
// ---------------------------------------------------------------------------------------------

/** "Hot" and "Mk II" of a rung (the pattern's `patterns.json` name; the key when the file lacks it). */
function rungNames(id: PatternUnlockId, unlocks: UnlockData): { pattern: string; mk: string } {
  const dot = id.lastIndexOf('.')
  const key = id.slice(0, dot)
  const rung: PatternRung = id.slice(dot + 1) === 'mk2' ? 'mk2' : 'mk3'
  const name = (PATTERN_IDS as readonly string[]).includes(key) ? unlocks.patterns.patterns[key as PatternId]?.name : undefined
  return { pattern: name ?? key, mk: PATTERN_TEXT.rungs[rung] }
}

/**
 * The notice after Study opens `pattern`'s `rung`, naming each module it opens as the Fabricate rows do (PG-2 R2-9):
 * "Pattern open: PDC Mk II (Hot) and Laser Mk II (Hot). Fabricate them here from their slots." The pattern's name and
 * rung stand in when its `patterns.json` entry is missing.
 */
export function studyResultLine(pattern: PatternId, rung: PatternRung, data: Pick<M2Data, 'modules'>, unlocks: UnlockData = unlockData()): string {
  const kinds = unlocks.patterns.patterns[pattern]?.modules ?? []
  const mk = rung === 'mk2' ? 2 : 3
  const names = kinds.map((kind) => resolveModule(data, patternModuleId(kind, mk, pattern), unlocks.patterns).name)
  if (names.length === 0) {
    const r = rungNames(unlockId(pattern, rung), unlocks)
    return fill(PATTERN_TEXT.opened, { modules: `${r.pattern} ${r.mk}` })
  }
  const last = names[names.length - 1]
  const modules = names.length === 1 ? last : fill(PATTERN_TEXT.listAnd, { rest: names.slice(0, -1).join(PATTERN_TEXT.listComma), last })
  return fill(names.length === 1 ? PATTERN_TEXT.opened : PATTERN_TEXT.openedMany, { modules })
}

/**
 * The notice after a data core is read (`after`: the Career once the core became a fragment): the fragment count and
 * either the rung it just opened ("Pattern open: Old pattern Mk II"), how many more open the next rung of the ladder
 * ("2 more to open Old pattern Mk II"), or the count alone once the ladder is done.
 */
export function fragmentResultLine(after: Career, cap: number, unlocks: UnlockData = unlockData()): string {
  const have = after.blueprints?.fragments ?? 0
  const per = unlocks.blueprints.fragmentsPerRung
  const ladder = fragmentLadder(unlocks)
  const base = { have, cap }
  const reached = Math.floor(have / per)
  const opened = have > 0 && have % per === 0 ? ladder[reached - 1] : undefined
  if (opened !== undefined) return fill(PATTERN_TEXT.fragmentsOpened, { ...base, ...rungNames(opened, unlocks) })
  const next = ladder[reached]
  if (next === undefined || have >= cap) return fill(PATTERN_TEXT.fragmentsAll, base)
  return fill(PATTERN_TEXT.fragmentsLeft, { ...base, left: (reached + 1) * per - have, ...rungNames(next, unlocks) })
}
